import { type PowerSyncDrizzleDB, user } from "@mealplan/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below go through `patchRowVerified` ─────────────────
 * (updated 2026-08-13 after the `ThemeProvider` seed guard)
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync view can
 * match ZERO rows and return "success" while the newly created row is still traveling
 * to the server (see `../powersync/verified-write.ts`). The `user` exclusion
 * other repos in the portfolio apply ("the server creates the row on
 * registration, so it is settled by the time the user changes preferences") does
 * NOT apply here, for TWO independent reasons—one closed, one still active:
 *
 *   1. (CLOSED, `apps/web/src/providers/theme-provider.tsx`) Until now, the
 *      `createEffect` that seeds the local `user` row — "Ensure the
 *      current user has a settings record" — ran for ANY `uid`, not
 *      just the guest. For a newly registered real account whose server row
 *      had not yet synced down, that local INSERT raced the real
 *      row in transit (which is why it catches `UNIQUE constraint failed`: sometimes
 *      the local INSERT won the race, sometimes the server did). Onboarding
 *      changed language/theme immediately after that seed, in the SAME session—the
 *      exact window this file measures in
 *      `__tests__/theme-provider-seed-window.test.ts`. The `uid !==
 *      VISITOR_ID` guard in `theme-provider.tsx` closed this: the local INSERT now
 *      occurs only for the guest, which never has a server row to
 *      race against.
 *   2. (STILL ACTIVE, generic) Closing (1) does NOT make
 *      `patchRowVerified` unnecessary here. `db` (the PowerSync accessor) becomes non-null
 *      as soon as local SQLite opens, not when initial sync finishes;
 *      `userId()` becomes active as soon as the session arrives from the server (a
 *      REST call independent of the PowerSync stream)—both BEFORE the
 *      checkpoint brings the `user` row the server always creates. An
 *      `UPDATE` triggered in that window (changing language/theme during onboarding,
 *      with or without (1)'s phantom seed in between) matches ZERO rows and
 *      silently "succeeds". This is what `patchRowVerified`
 *      protects today: it is not a patch left attached to an already closed bug; it defends
 *      the checkpoint window itself. If it is ever removed, it should be
 *      because it is proven that THIS window no longer exists, not because
 *      `ThemeProvider` stopped seeding for real accounts.
 *
 * Separate note: the upserts in `../shopping/mutations.ts` and `../shopping/generate.ts`
 * (Class 2 in `../powersync/verified-write.ts`, closed with
 * `readMatchingRowVerified`) are a DIFFERENT problem—they decide INSERT-vs-reuse
 * through a `SELECT` with no `id` known in advance. The startup guard in
 * `ThemeProvider` does not affect them: the `user` seed uses a known id
 * (`uid`) and was already protected by (1)'s `UNIQUE constraint failed` catch.
 *
 * ── Why ALL mutations below include `.where(eq(user.id, userId))` ───────────
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * meaning that, in guest mode, the UPDATE went out **without `WHERE`** → it rewrote ALL
 * `user` rows in the local database. The branch existed for symmetry with
 * `getUserThemeSettingsQuery`, which for the guest reads with `.limit(1)` and no
 * filter; but a read `limit(1)` and an unfiltered UPDATE are not the same
 * bet: the former can pick the wrong row, while the latter crushes all of them.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade, the
 * guest database CONNECTS to the real account's connector and
 * self-subscribes to all streams—including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each touched
 * row enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid`
 * is exactly `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME
 * row that the unfiltered branch intended to touch when the table had only one
 * row—identical behavior in the normal case, bounded otherwise. It is
 * also the form `updateUserFontFamilyMutation` already used, which never had
 * a guest branch.
 *
 * Covered by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
